import {
  Box,
  Breadcrumbs,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  Chip,
  Grid,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Link as RouterLink,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import CustomerSamplesTable from "../../components/Customer/CustomerSamplesTable";
import CustomerSchemaCoverageTable from "../../components/Customer/CustomerSchemaCoverageTable";
import { useError } from "../../contexts/ErrorContext";
import useApi from "../../hooks/useGaneshApi";
import { useQuery } from "@tanstack/react-query";

import {
  RiCheckboxBlankCircleLine,
  RiCheckboxCircleFill,
} from "@remixicon/react";
import { Customer_T } from "../../types/Customer";
import { formatDateHumanReadable } from "utils/formatting";
import { differenceInDays } from "date-fns";
import DeliveriesTable from "../../components/Delivery/DeliveriesTable";

const CustomerDetailPage: React.FC = () => {
  const { customer_id } = useParams<{ customer_id: string }>();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedTabName, setSelectedTabName] = useState<string>("Deliveries");

  const { showError } = useError();
  const { apiRequest } = useApi();

  const {
    data: customerMetadata,
    isLoading: metadataLoading,
    isError,
    error,
  } = useQuery<Customer_T, Error>({
    queryKey: ["customer", customer_id],
    queryFn: async () => {
      const response = await apiRequest<Customer_T>(
        `/v1/customers/${customer_id}`
      );
      if (response.error) {
        throw new Error(response.error);
      }
      if (!response.data) {
        throw new Error(`Customer with ID ${customer_id} not found.`);
      }
      return response.data;
    },
    enabled: !!customer_id,
  });

  const licenseDate = customerMetadata?.next_license_end_date
    ? new Date(customerMetadata.next_license_end_date)
    : null;
  const isLicenseExpiringSoon =
    licenseDate &&
    differenceInDays(licenseDate, new Date()) <= 90 &&
    differenceInDays(licenseDate, new Date()) >= 0;

  useEffect(() => {
    document.title = `${customerMetadata?.name || ""} - ${selectedTabName}`;
  }, [location, customerMetadata, selectedTabName]);

  if (isError) {
    showError(
      `Failed to fetch customer metadata for ${customer_id}: ${error.message}`
    );
  }

  const getStatusIcon = (status?: string) => {
    if (status === "Current") {
      return <RiCheckboxCircleFill color="green" />;
    } else if (status === "Legacy") {
      return <RiCheckboxCircleFill color="grey" />;
    } else {
      return <RiCheckboxBlankCircleLine color="grey" />;
    }
  };

  useEffect(() => {
    // Set selected tab and name based on URL path
    if (location.pathname.endsWith("deliveries")) {
      setSelectedTab(0);
      setSelectedTabName("Deliveries");
    } else if (location.pathname.endsWith("schema")) {
      setSelectedTab(2);
      setSelectedTabName("Schema Analysis");
    } else if (location.pathname.endsWith("samples")) {
      setSelectedTab(1);
      setSelectedTabName("Samples");
    }
  }, [location.pathname]);

  if (metadataLoading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" color="text.secondary">
          Loading customer details...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <RouterLink
          to="/customers"
          style={{ textDecoration: "none", color: "inherit" }}
        >
          Customers
        </RouterLink>
        <Typography color="text.primary">
          {customerMetadata?.name || "Customer"}
        </Typography>
      </Breadcrumbs>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ flexGrow: 1 }}
        >
          {customerMetadata?.name} (ID: {customerMetadata?.id})
        </Typography>
        <Chip
          icon={getStatusIcon(customerMetadata?.customer_status)}
          label={customerMetadata?.customer_status || "Unknown"}
          variant="outlined"
          sx={{ ml: 2 }}
        />
      </Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Typography>
            <strong>Active Deliveries:</strong>{" "}
            {customerMetadata?.active_delivery_count ?? "N/A"}
          </Typography>
          <Typography>
            <strong>All Time Deliveries:</strong>{" "}
            {customerMetadata?.all_delivery_count ?? "N/A"}
          </Typography>
          <Typography>
            <strong>Active Fulfillment Groups:</strong>{" "}
            {customerMetadata?.active_fg_count ?? "N/A"}
          </Typography>
          <Typography>
            <strong>All Time Samples:</strong>{" "}
            {customerMetadata?.all_samples_count ?? "N/A"}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography>
            <strong>Active Licenses:</strong>{" "}
            {customerMetadata?.active_license_count ?? "N/A"}
          </Typography>
          <Typography sx={{ color: isLicenseExpiringSoon ? "red" : "inherit" }}>
            <strong>Next License End Date:</strong>{" "}
            {licenseDate ? formatDateHumanReadable(licenseDate) : "N/A"}
          </Typography>
          <Typography>
            <strong>Fulfillment Destination Type:</strong>{" "}
            {customerMetadata?.fulfillment_destination_type ?? "N/A"}
          </Typography>
          <Typography>
            <strong>Data Format:</strong>{" "}
            {customerMetadata?.delivery_data_format ?? "N/A"}
          </Typography>
        </Grid>
      </Grid>
      <Tabs
        value={selectedTab}
        onChange={(e, newValue) => setSelectedTab(newValue)}
        sx={{ marginBottom: 2 }}
      >
        <Tab
          label="Deliveries"
          component={RouterLink}
          to={`/customer/${customer_id}/deliveries`}
        />
        <Tab
          label="Samples"
          component={RouterLink}
          to={`/customer/${customer_id}/samples`}
        />
        <Tab
          label="Schema Analysis"
          component={RouterLink}
          to={`/customer/${customer_id}/schema`}
        />
      </Tabs>
      <Routes>
        <Route
          path="/"
          element={
            <Navigate
              to={`/customer/${customer_id}/deliveries`}
              replace={true}
            />
          }
        />
        <Route
          path="deliveries"
          element={<DeliveriesTable customerId={customer_id} />}
        />
        <Route
          path="samples"
          element={<CustomerSamplesTable customerId={customer_id} />}
        />
        <Route
          path="schema"
          element={<CustomerSchemaCoverageTable customerId={customer_id} />}
        />
      </Routes>
    </Box>
  );
};

export default CustomerDetailPage;
